// eslint-disable-next-line no-restricted-imports
import { Box, Flex, Skeleton, Stack, Text } from '@mantine/core';
import { memo } from 'react';

import { useIsFeatureEnabled } from '~/hooks/useIsFeatureEnabled';
import useIsUserAdmin from '~/domains/payments/hooks/useIsUserAdmin';
import { FEATURE } from '~/common/enums/feature.enum';
import type { GetPromoContestsItem } from '~/domains/cms/promo/contests/getPromoContests';
import ConnectedContestCard from '~/domains/contest/domains/common/components/ContestCard/ConnectedContestCard';
import { reportEvent } from '~/domains/analytics';

import { useLazyLoadContestSectionInfinite } from '../hooks/useLazyLoadContestSectionInfinite';

import HomeCarousel from './HomeCarousel';
import { HomeSectionTitle, HomeViewAllLink } from './sectionComponents';
import ContestCardPlaceholder from './ContestCardPlaceholder';

function HomeContest({
  contestIds,
  title,
  viewLink,
  isShowContestThumbnailEnabled,
  registerSection,
}: GetPromoContestsItem & {
  isShowContestThumbnailEnabled: boolean;
  registerSection: (
    sectionTitle: string,
    refreshInfo: {
      queryKey: string[];
      isVisible: boolean;
      refetch: () => void;
      invalidateSection: () => void;
      clearSection: () => void;
    }
  ) => (() => void) | void;
}) {
  const isAdmin = useIsUserAdmin();
  const isCountdownPillEnabled = useIsFeatureEnabled(FEATURE.ENABLE_CONTEST_COUNTDOWN_TIMER);

  const {
    elementRef,
    scrollContainerRef,
    loaderRef,
    hasBeenVisible,
    contests,
    isInitialLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    totalContestIds,
  } = useLazyLoadContestSectionInfinite({
    contestIds,
    sectionTitle: title,
    includeFull: isAdmin,
    rootMargin: '200px',
    threshold: 0.1,
    onRegisterSection: registerSection,
  });

  if (!contestIds?.length || error) return null;

  if (!hasBeenVisible) {
    return (
      <Stack spacing="lg" ref={elementRef}>
        <Skeleton h={28} w={230} />
        <Skeleton mih={170} />
      </Stack>
    );
  }

  if (!isInitialLoading && contests.length === 0) return null;

  return (
    <Stack key={title} spacing={0} ref={elementRef}>
      <Flex justify="space-between" w="100%" align="baseline">
        <HomeSectionTitle>{title}</HomeSectionTitle>
        <HomeViewAllLink
          href={viewLink}
          onClick={() => reportEvent('Home > View More - Home Contests', { title })}
        />
      </Flex>
      <Box p={0} component="ul" m={0} w="100%" ref={scrollContainerRef}>
        {isInitialLoading ? (
          <ContestCardPlaceholder count={Math.min(10, totalContestIds)} />
        ) : (
          <HomeCarousel>
            {contests.map((contest, index) => (
              <HomeCarousel.Slide key={contest.id}>
                <ConnectedContestCard
                  contest={contest}
                  isContestThumbnailShown={isShowContestThumbnailEnabled}
                  isNotAllowedInLocation={contest.is_allowed_in_location === false}
                  isBadgeIconSectionEnabled={false}
                  isBannerEnabled
                  isCountdownPillEnabled={isCountdownPillEnabled}
                  promoSource={`home_contests_${title}`}
                  promoIndex={index + 1}
                />
              </HomeCarousel.Slide>
            ))}

            {isFetchingNextPage &&
              Array.from({ length: 3 }, (_, index) => (
                <HomeCarousel.Slide key={`loading-${index}`}>
                  <Skeleton height={200} radius="sm" />
                </HomeCarousel.Slide>
              ))}
          </HomeCarousel>
        )}

        {hasNextPage && isFetchingNextPage && (
          <Box ref={loaderRef} style={{ height: 20, margin: /* eslint-disable-line local-rules/use-theme-tokens */ '10px 0' }}>
            <Text size="sm" color="dimmed" ta="center">
              Loading more contests...
            </Text>
          </Box>
        )}
      </Box>
    </Stack>
  );
}

const memoizedHomeContest = memo(HomeContest);
export default memoizedHomeContest;
